import {
  bulkChanges,
  grantedLookup,
  groupPermissions,
  type PermissionChange,
  useAdminPermissions,
  useBulkUpdateRolePermissions,
  useUpdateRolePermission,
} from '../../../features/admin/use-admin-permissions'
import { AdminPage, adminError } from './admin-bits'
import { Inline } from './controls'
import { mono, Note, Section } from './ui'

const linkBtn =
  'cursor-pointer text-[11px] text-[var(--color-accent)] underline decoration-[var(--color-border)] underline-offset-2 hover:decoration-[var(--color-accent)] disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-[var(--color-accent)]'

export function LedgerAdminPermissions() {
  const q = useAdminPermissions()
  const one = useUpdateRolePermission()
  const bulk = useBulkUpdateRolePermissions()
  const roles = q.data?.roles ?? []
  const groups = groupPermissions(q.data?.permissions ?? [])
  const granted = grantedLookup(roles)
  const err = one.error ?? bulk.error
  const apply = (changes: PermissionChange[]) => {
    if (changes.length) bulk.mutate({ changes })
  }
  return (
    <AdminPage
      title="Permissions"
      standfirst="Which roles may do what. Grants are applied at once, but can take up to five minutes to reach every session."
      loading={q.isLoading}
      error={!!q.error}
    >
      {err ? (
        <div className="mt-4">
          <Inline tone="bad">{adminError(err)} The change was reverted.</Inline>
        </div>
      ) : null}
      {groups.length === 0 ? <Note>No permissions defined.</Note> : null}
      {groups.map((g) => {
        const ids = g.permissions.map((p) => p.id)
        return (
          <Section key={g.groupTag} title={g.groupTag}>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="py-2.5 pr-4 text-left text-[12px] font-medium text-[var(--color-muted)]">
                      Permission
                    </th>
                    {roles.map((r) => (
                      <th key={r.id} className="px-3 py-2.5 text-center text-[12px] font-medium">
                        <div className="text-[13px] text-[var(--color-fg)]">{r.name}</div>
                        <div className="flex justify-center gap-1.5">
                          <button
                            type="button"
                            className={linkBtn}
                            disabled={bulk.isPending}
                            onClick={() => apply(bulkChanges(roles, r.id, ids, true))}
                          >
                            all
                          </button>
                          <span aria-hidden="true" className="text-[11px] text-[var(--color-muted)]">
                            /
                          </span>
                          <button
                            type="button"
                            className={linkBtn}
                            disabled={bulk.isPending}
                            onClick={() => apply(bulkChanges(roles, r.id, ids, false))}
                          >
                            none
                          </button>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {g.permissions.map((p) => (
                    <tr key={p.id} className="border-t border-[var(--color-border)]">
                      <td className="py-2.5 pr-4 text-[13px]" style={mono}>
                        {p.name}
                      </td>
                      {roles.map((r) => {
                        const on = granted.get(r.id)?.has(p.id) ?? false
                        return (
                          <td key={r.id} className="px-3 text-center">
                            <input
                              type="checkbox"
                              className="size-4 cursor-pointer accent-[var(--color-accent)]"
                              aria-label={`${r.name}: ${p.name}`}
                              checked={on}
                              onChange={() =>
                                one.mutate({ roleId: r.id, permissionId: p.id, action: on ? 'revoke' : 'grant' })
                              }
                            />
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )
      })}
    </AdminPage>
  )
}
